import { useEffect, useMemo } from 'react';
import { useBasket } from '../../bff-api/baskets/useBasket';
import { useCustomerGet } from '../../bff-api/customers/useCustomerGet';
import { useEstimatedDeliveryDate } from '../../bff-api/delivery/useEstimatedDeliveryDate';
import { useCustomerGeoLocationStore } from '../../bff-api/stores/useCustomerGeoLocationStore';
import { isBasketEmpty } from '../../bff-api/utils/basket-utils';
import { isServer } from '../../utils/utils';
import { useUserZipCode } from '../useUserZipCode';
import {
    getPageType,
    isNotEventTriggered,
    removeEventSensitiveFields,
    removePageDataSensitiveFields,
} from './gtm-utils';
import { GTMEventName } from './model/GTMEventName';
import type { Basket, ProductItem } from '../../bff-api/baskets/model';
import type { Customer } from '../../bff-api/customers/model';
import type { Product, Products } from '../../bff-api/products/model';
import type { RegisterCustomerDTO } from '../../pages/account/model/RegisterCustomerDTO';
import type { GTMEvent } from './model/GTMEvent';
import type { GTMPageData } from './model/GTMPageData';

export interface GTM {
    init: () => void;
    onAddToBag: (productAdded: Product, basket: Basket) => void;
    onCheckout: () => void;
    onClickSearchSuggestion: (searchTerm: string, searchSuggestion: string) => void;
    onCompareProductsPageView: (products: Products) => void;
    onContinueToBilling: (user: Customer) => void;
    onPdpPageView: (product: Product) => void;
    onRemoveProductFromBag: (productItem: ProductItem, basket: Basket) => void;
    onSearch: (searchTerm: string) => void;
    onPwaError: (pwaError: unknown) => void;
    onUserRegistration: (data: RegisterCustomerDTO) => void;
    updatePageData: (data: GTMPageData) => void;
}

const updatePageData = (data: GTMPageData): void => {
    if (isServer) {
        return;
    }

    const filteredData = removePageDataSensitiveFields(data);

    window.pageData = {
        isPwa: true,
        ...window.pageData,
        ...filteredData,
    };
};

const updateDataLayer = (event: GTMEvent): void => {
    if (isServer || !window.dataLayer) {
        return;
    }

    const filteredEvent = removeEventSensitiveFields(event);

    window.dataLayer.push(filteredEvent);
};

/**
 * Custom React hook to work with Google Tag Manager.
 */
const useGTM = (): GTM => {
    const { data: bag } = useBasket();
    const { data: customer } = useCustomerGet();
    const { data: store } = useCustomerGeoLocationStore();
    const { data: zipCode } = useUserZipCode();
    const { data = {} } = useEstimatedDeliveryDate(isBasketEmpty(bag) ? '' : zipCode);
    const { edd } = data;

    const buildUser = useMemo(
        () => (): Customer | undefined => ({
            ...customer,
            c_zipCode: zipCode,
            c_nearestStoreID: store?.id,
            c_heapUserID: window.heap?.userId,
        }),
        [customer, zipCode, store],
    );

    useEffect(() => {
        if (bag) {
            updatePageData({ bag });
        }
    }, [bag]);

    useEffect(() => {
        const user = buildUser();
        if (user) {
            updatePageData({
                user,
            });
        }
    }, [buildUser, zipCode, store]);

    const init = (): void => {
        if (isServer) {
            return;
        }

        const pageType = getPageType();
        const user = buildUser();

        updatePageData({
            pageType,
            edd,
            pageName: document.title,
            user,
            bag,
        });
    };

    const onAddToBag = (product: Product, basket: Basket): void => {
        updateDataLayer({
            event: GTMEventName.ADD_TO_BAG,
            product,
            bag: basket,
        });

        updatePageData({ bag: basket });
    };

    const onCheckout = (): void => {
        updateDataLayer({
            event: GTMEventName.START_CHECKOUT,
        });
    };

    const onClickSearchSuggestion = (term: string, suggestionSelected: string): void => {
        updateDataLayer({
            event: GTMEventName.CLICK_SEARCH_SUGGESTION,
            search: {
                term,
                suggestionSelected,
            },
        });
    };

    const onCompareProductsPageView = (products: Products): void => {
        if (products.length && isNotEventTriggered(GTMEventName.COMPARE_PRODUCTS_PAGE_VIEW)) {
            updateDataLayer({
                event: GTMEventName.COMPARE_PRODUCTS_PAGE_VIEW,
                products,
            });

            updatePageData({ products });
        }
    };

    const onContinueToBilling = (user: Customer): void => {
        updateDataLayer({
            event: GTMEventName.ON_CONTINUE_TO_BILLING,
            user,
        });
    };

    const onPdpPageView = (product: Product): void => {
        updateDataLayer({
            event: GTMEventName.PDP_PAGE_VIEW,
            product,
        });
    };

    const onRemoveProductFromBag = (productItem: ProductItem, basket: Basket): void => {
        updateDataLayer({
            event: GTMEventName.REMOVE_PRODUCT_FROM_BAG,
            productItem,
            bag: basket,
        });

        updatePageData({ bag: basket });
    };

    const onSearch = (term: string): void => {
        updateDataLayer({
            event: GTMEventName.SEARCH,
            search: {
                term,
            },
        });
    };

    const onPwaError = (pwaError: unknown): void => {
        updateDataLayer({
            event: GTMEventName.PWA_ERROR,
            pwaError,
        });
    };

    const onUserRegistration = ({ firstName, lastName, email }: RegisterCustomerDTO): void => {
        updateDataLayer({
            event: GTMEventName.USER_REGISTRATION,
            user: {
                firstName,
                lastName,
                email,
            },
        });
    };

    return {
        init,
        onAddToBag,
        onClickSearchSuggestion,
        onCompareProductsPageView,
        onCheckout,
        onContinueToBilling,
        onPdpPageView,
        onRemoveProductFromBag,
        onSearch,
        onPwaError,
        onUserRegistration,
        updatePageData,
    };
};

export { useGTM };
